import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';

import { isRangeSupported } from '../../_utils/isRangeSupported';

import { PriceType } from '@cast/shared-types';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  mapCostReportChartDataToTableData,
  transformWorkloadCostData,
  CostReportDataByPeriod,
} from '@cast/split-me/features/cluster/cost-report';
import { useWorkloadCostReportQuery } from '@cast/split-me/hooks';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

export const useInventoryWorkloadComputeData = () => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { from, to, timeZone } = useInventoryWorkloadsTimeRangeSelection();

  const fromParam = from ? dayjs.utc(from) : dayjs.utc(0);
  const toParam = to ? dayjs.utc(to) : dayjs.utc(0);
  const hasRange = !!from && !!to;
  const rangeSupported = hasRange && isRangeSupported(from, to);

  const {
    workload: workloadData,
    isLoading,
    error,
    refetch,
  } = useWorkloadCostReportQuery({
    clusterId: cluster.id,
    namespace: workload?.namespace || '',
    workloadType: workload?.kind || '',
    workloadName: workload?.name || '',
    range: [fromParam, toParam],
    useListingPrices: false,
    includeIdleCosts: false,
    enabled:
      rangeSupported &&
      !!workload?.namespace &&
      !!workload?.kind &&
      !!workload?.name,
  });

  const data = useMemo(() => {
    if (!workloadData || !hasRange) {
      return [];
    }

    const [, chartData] = transformWorkloadCostData(
      [fromParam, toParam],
      workloadData,
      timeZone
    );

    return mapCostReportChartDataToTableData({
      chartData: chartData as CostReportDataByPeriod[],
      timezone: timeZone,
      storageCostGranularity: PriceType.HOURLY,
      projectEndOfDayCost: false,
    });
  }, [workloadData, fromParam, toParam, timeZone]);

  return {
    data,
    isLoading,
    error,
    refetch,
    isRangeSupported: rangeSupported,
  };
};
